// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $Model,
  $ModelUpsertParams,
} from 'common.data';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  Table,
} from 'reactstrap';
import {
  DeleteDialog,
  FormDialog,
  Loader,
  SectionHeader,
  TdDate,
} from '@vroskus/library-components';

// Helpers
import _ from 'lodash';
import {
  cleanFormValues,
  isDiff,
} from '@vroskus/library-helpers/dist/browser';
import {
  regattaStatusLabel,
} from 'common.language';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
} from '../../../../helpers';

// Components
import EmptyTable from '../../../common/EmptyTable';
import ChampionshipRegattaFields from '../../../fields/ChampionshipRegattaFields';
import ThQty from '../../../common/ThQty';

// Services
import Services from '../../../../services';

// Enums
import {
  regattaStatusClass,
} from '../../../../enums';

// Types
type $Props = $Translate & {
  id: string;
  item: $Model<'Championship'>;
};

type $State = {
  data: Array<$Model<'Regatta'>>;
  loading: boolean;
  resourceData: Array<$Model<'Regatta'>>;
};

class ChampionshipRegattasSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
      resourceData: [],
    };
  }

  componentDidMount(): void {
    this.getData();
    this.getResourceData();
    this.setupSocket();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      item,
    } = this.props;

    if (isDiff(
      item.id,
      prevProps.item.id,
    )) {
      this.getData();
      this.getResourceData();
    }
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const {
          item,
        } = this.props;

        if (item.id === data.ChampionshipId && data.modelName === 'Regatta') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      item,
    } = this.props;

    const ChampionshipId = item.id;

    await defaultGetData({
      action: async () => {
        await this.setStateAsync({
          loading: true,
        });

        const data = await Services.Api.getManyChampionshipRegatta({
          ChampionshipId,
        });

        await this.setStateAsync({
          data,
          loading: false,
        });
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  async getResourceData(): Promise<void> {
    const {
      item,
    } = this.props;

    const {
      RegionSeasonId,
    } = item;

    await defaultGetData({
      action: async () => {
        const resourceData = await Services.Api.getResourceRegatta({
          RegionSeasonId,
        });

        await this.setStateAsync({
          resourceData,
        });
      },
    });
  }

  renderHeader() {
    const {
      item,
      t,
    } = this.props;
    const {
      data,
      resourceData,
    } = this.state;

    const ChampionshipId = item.id;

    const newItem: $ModelUpsertParams<'ChampionshipRegatta'> = {
      ChampionshipId,
      RegattaId: '',
    };

    return (
      <SectionHeader
        bottomBorder
        iconClass={'icon-flag'}
        rightButtons={(
          <FormDialog
            fields={(
              <ChampionshipRegattaFields
                item={newItem}
                resourceData={_.filter(
                  resourceData,
                  (i) => !_.find(
                    data,
                    {
                      id: i.id,
                    },
                  ),
                )}
              />
            )}
            id={'create-championship-regatta-dialog'}
            labelCloseButton={t('Close')}
            labelSaveButton={t('Save')}
            onAction={(values) => defaultUpsertItem({
              action: async () => {
                const params: {
                  ChampionshipId: string;
                  RegattaId: string;
                } = cleanFormValues<$Model<'ChampionshipRegatta'>>({
                  ...newItem,
                  ...values,
                });

                await Services.Api.addChampionshipRegatta(params);
              },
            })}
            subTitle={t('New')}
            title={t('Championship regatta')}
          >
            <Button
              color={'info'}
              id={'create-championship-regatta-button'}
              size={'sm'}
              title={t('Add regatta')}
            >
              {t('Add')}
            </Button>
          </FormDialog>
        )}
        title={t('Championship regattas')}
      />
    );
  }

  renderButtons(dataItem: $Model<'Regatta'>) {
    const {
      item,
      t,
    } = this.props;

    const ChampionshipId = item.id;
    const {
      id,
    } = dataItem;

    return (
      <div className={'d-flex justify-content-end'}>
        <DeleteDialog
          content={t('Do you really want to remove?')}
          id={`delete-championship-regatta-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelDeleteButton={t('Delete')}
          onAction={() => defaultDeleteItem({
            action: async () => {
              const params: {
                ChampionshipId: string;
                RegattaId: string;
              } = {
                ChampionshipId,
                RegattaId: id,
              };

              await Services.Api.removeChampionshipRegatta(params);
            },
          })}
          subTitle={dataItem.Name}
          title={t('Championship regatta')}
        >
          <Button
            color={'danger'}
            id={`delete-championship-regatta-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Remove')}
          </Button>
        </DeleteDialog>
      </div>
    );
  }

  renderTable() {
    const {
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    return data.length === 0 ? (
      <EmptyTable />
    ) : (
      <Table responsive>
        <thead>
          <tr>
            <th>{t('Status')}</th>
            <th>{t('Name')}</th>
            <th>{t('Start date')}</th>
            <th>{t('End date')}</th>
            <th>{t('Location')}</th>
            <ThQty value={data.length} />
          </tr>
        </thead>
        <tbody>
          {data.map((dataItem: $Model<'Regatta'>) => (
            <tr key={dataItem.id}>
              <td>
                <span className={`badge rounded-pill ${regattaStatusClass[dataItem.Status]}`}>
                  {t(regattaStatusLabel[dataItem.Status])}
                </span>
              </td>
              <td>{dataItem.Name}</td>
              <TdDate value={dataItem.StartDate} />
              <TdDate value={dataItem.EndDate} />
              <td>{dataItem.Location}</td>
              <td>
                {this.renderButtons(dataItem)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Loader
        loading={loading === true}
        overlay
      >
        {this.renderTable()}
      </Loader>
    );
  }

  render() {
    const {
      id,
    } = this.props;

    return (
      <Card id={id}>
        {this.renderHeader()}
        {this.renderContent()}
      </Card>
    );
  }
}

export default ChampionshipRegattasSectionView;
